/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import Progress from 'redux-progress';

import config from 'appconfig';
import { State } from 'model/State';
import { getCurrentContactDetails } from 'modules/auth/selectors';
import selectIsAuthenticated from 'modules/auth/selectors/isAuthenticated';
import { isIntercomEnabledProduct } from 'modules/configure/duck/util';
import { CurrentContact } from 'modules/auth/model/CurrentContact';
import { reportLogOnly } from 'util/errorReporter';
import { ProductKey } from 'data/products/keys';
import { checkFeatureGate, FeatureFlags } from 'util/statsig';
import { SalesChannelOcpWidget } from 'omni-channel';

type IntercomConfig = {
  // eslint-disable-next-line camelcase
  app_id: string;
  email?: string;
  // eslint-disable-next-line camelcase
  user_name?: string;
};

type props = {
  productKey: ProductKey;
  currentContactsProgress: Progress<CurrentContact>;
  isAuthenticated: boolean;
  visible?: boolean;
};

function IntercomConfiguration(props: props) {
  const { currentContactsProgress, isAuthenticated, visible = true } = props;
  const intercomEnabledProduct = isIntercomEnabledProduct(props.productKey);
  const isOmniChannelEnabled = checkFeatureGate(FeatureFlags.CART_ENABLE_OMNI_CHANNEL_CHAT);

  const [userContext, setUserContext] = useState<{ email?: string; userName?: string }>({});

  const getIntercomConfig = (currentContacts: CurrentContact): IntercomConfig => {
    let intercomConfig: IntercomConfig = { app_id: config.intercomAppID };
    const contactFirstName = currentContacts?.contactDetails?.firstName;
    const contactEmail = currentContacts?.contactDetails?.email;

    if (contactFirstName && contactEmail) {
      intercomConfig = {
        ...intercomConfig,
        ...{
          email: contactEmail,
          user_name: contactFirstName,
        },
      };
    }
    return intercomConfig;
  };

  useEffect(() => {
    currentContactsProgress.ifSuccess((currentContacts) => {
      const contactFirstName = currentContacts?.contactDetails?.firstName;
      const contactEmail = currentContacts?.contactDetails?.email;

      // Store user context for OmniChannel
      if (contactEmail) {
        setUserContext({
          email: contactEmail,
          userName: contactFirstName,
        });
      }

      // If OmniChannel is enabled, skip Intercom initialization
      if (isOmniChannelEnabled) {
        return;
      }

      const intercomConfig = getIntercomConfig(currentContacts);

      if (intercomEnabledProduct) {
        try {
          window.Intercom('boot', intercomConfig);
          window.Intercom('trackEvent', 'Page-opened');
        } catch (error) {
          reportLogOnly('ProductConfigurationPage', {
            intercomConfig,
            error,
            productKey: props.productKey,
          });
        }
      }
    });
  }, [props.currentContactsProgress, isOmniChannelEnabled]);

  // If OmniChannel is enabled, render OmniChannel instead of Intercom.
  if (isOmniChannelEnabled && intercomEnabledProduct) {
    return (
      <SalesChannelOcpWidget
        email={userContext.email}
        userName={userContext.userName}
        isAuthenticated={isAuthenticated}
        visible={visible}
      />
    );
  }

  return <></>;
}

const mapStateToProps = (state: State) => ({
  currentContactsProgress: getCurrentContactDetails(state),
  isAuthenticated: selectIsAuthenticated(state),
});

export default connect(mapStateToProps)(IntercomConfiguration);
